/**
 * Module dependencies.
 */

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from 'app/components/ui/alert-dialog';
import { Button } from 'app/components/ui/button';
import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from 'app/components/ui/sheet';
import { cn } from 'app/lib/utils';
import { X } from 'lucide-react';
import React, { ReactNode, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DrawerBodyProvider } from './drawer-context';

/**
 * `Props` type.
 */

type Props = {
  className?: string;
  isOpen: boolean;
  title?: ReactNode;
  buttons?: ReactNode;
  isLoading?: boolean;
  maskClosable?: boolean;
  width?: number | string;
  children: JSX.Element;
  onClose?: () => void;
};

/**
 * Export `Drawer` component.
 *
 * Right-side drawer with a scrollable body. Mirrors the `Modal` organism API so
 * the same call sites (crud add/edit, row actions) can switch between the two.
 *
 * Closing is deliberate only: the backdrop never dismisses the drawer, and if
 * the form inside has unsaved changes, closing asks for confirmation first.
 */

export function Drawer(props: Props): JSX.Element {
  const { className, title, buttons, width, children, onClose, isOpen } = props;
  const [translate] = useTranslation();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const dirtyCheckRef = useRef<(() => boolean) | null>(null);
  const resolvedWidth = typeof width === 'number' ? `${width}px` : width ?? '480px';

  const registerDirtyCheck = useCallback((check: (() => boolean) | null) => {
    dirtyCheckRef.current = check;
  }, []);

  // A close attempt: confirm first when the form has unsaved changes.
  const requestClose = useCallback(() => {
    if (dirtyCheckRef.current?.()) {
      setConfirmOpen(true);

      return;
    }

    onClose?.();
  }, [onClose]);

  const confirmClose = useCallback(() => {
    setConfirmOpen(false);
    onClose?.();
  }, [onClose]);

  return (
    <>
      <Sheet
        onOpenChange={open => {
          // Backdrop click, Escape and the Close button all route through
          // `requestClose`, which asks for confirmation when the form is dirty.
          if (!open) {
            requestClose();
          }
        }}
        open={isOpen}
      >
        <SheetContent
          className={cn('flex flex-col gap-0 border-0 p-0 sm:max-w-none', className)}
          hideClose
          onInteractOutside={event => {
            // Radix Select/Popover/Dropdown render in a portal outside the drawer,
            // so choosing an option counts as an outside interaction. Keep the
            // drawer open in that case; a genuine backdrop click falls through to
            // `onOpenChange` → `requestClose` (which confirms unsaved changes).
            const detail = (event as unknown as { detail?: { originalEvent?: Event } }).detail;
            const target = ((detail?.originalEvent?.target ?? event.target) as HTMLElement | null) ?? null;

            if (target?.closest?.('[data-radix-popper-content-wrapper],[role="listbox"],[role="menu"]')) {
              event.preventDefault();
            }
          }}
          side={'right'}
          style={{ maxWidth: '100vw', width: resolvedWidth }}
        >
          {/* Sticky header: title on the left, a labelled Close on the right
              (instead of the corner-only X, which was easy to miss). Radix needs
              a title for accessibility, so an untitled drawer gets a hidden one. */}
          <SheetHeader
            className={
              'flex shrink-0 flex-row items-center justify-between gap-4 space-y-0 border-b border-border px-6 py-4'
            }
          >
            <SheetTitle className={cn(!title && 'sr-only')}>{title ?? translate('common.actions.close')}</SheetTitle>

            <Button
              className={'-mr-2 shrink-0 gap-1.5 text-muted-foreground hover:text-foreground'}
              onClick={requestClose}
              size={'sm'}
              type={'button'}
              variant={'ghost'}
            >
              <X className={'h-4 w-4'} />

              {translate('common.actions.close')}
            </Button>
          </SheetHeader>

          {/* Scrollable body. A CRUD form rendered here reads `useInDrawer` and
              pins its submit button to the bottom of this scroll area, and
              registers its dirty state so closing can warn about unsaved data. */}
          <div className={'min-h-0 flex-1 overflow-y-auto px-6 py-5'}>
            <DrawerBodyProvider registerDirtyCheck={registerDirtyCheck}>{children}</DrawerBodyProvider>
          </div>

          {buttons && <SheetFooter className={'shrink-0 border-t border-border px-6 py-4'}>{buttons}</SheetFooter>}
        </SheetContent>
      </Sheet>

      <AlertDialog onOpenChange={setConfirmOpen} open={confirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{translate('common.drawer.discardTitle')}</AlertDialogTitle>

            <AlertDialogDescription>{translate('common.drawer.discardDescription')}</AlertDialogDescription>
          </AlertDialogHeader>

          <AlertDialogFooter>
            <AlertDialogCancel>{translate('common.drawer.keepEditing')}</AlertDialogCancel>

            <AlertDialogAction
              className={'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90'}
              onClick={confirmClose}
            >
              {translate('common.drawer.discard')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
